import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import IntegerInput from 'components/field/IntegerInput';
import StringInput from 'components/field/StringInput';
import TextArea from 'components/field/TextArea';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Image from 'components/global/Image';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import { ADMIN, AUTOCOMPLETE, ENUMS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

const defaultColSpan = {
	md: 6
};
const fullColSpan = {
	sm: 12
};
const AddPackageModal = NiceModal.create(({ id, modalName }) => {
	const modal = useModal();
	const dispatch = useDispatch();
	const axios = useAxiosPrivate();
	const { showSuccess, showError } = useAlert();
	const { closeModal } = useModalRouter();
	const darkMode = useSelector((state) => state.layout.darkMode);
	const defaultImage = darkMode
		? 'https://storage.googleapis.com/tryoto-public/public/assets/images/emptySalesChannel-darkMode.png'
		: 'https://storage.googleapis.com/tryoto-public/public/assets/images/emptySalesChannel.png';

	const schema = useMemo(
		() =>
			yup.object().shape({
				packageName: yup.string().required(),
				packageRoleID: yup.number().required(),
				monthlyDiscount: yup.number().nullable(),
				monthlyPrice: yup.number().nullable(),
				yearlyDiscount: yup.number().nullable(),
				yearlyPrice: yup.number().nullable(),
				countryId: yup.number().required(),
				currency: yup.string().required(),
				dailyPrice: yup.number().nullable(),
				defaultModuleIds: yup.array().of(yup.number()).nullable(),
				subscriptionModuleIds: yup.array().of(yup.number()).nullable(),
				tier: yup.number().required(),
				infoJson: yup.string().nullable().json(),
				packageIcon: yup.string().nullable()
			}),
		[]
	);

	const handleCloseModal = useCallback(() => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	}, [modalName]);

	const fillForm = useCallback(async () => {
		const defaultValues = {
			packageName: null,
			packageRoleID: null,
			monthlyDiscount: null,
			monthlyPrice: null,
			yearlyDiscount: null,
			yearlyPrice: null,
			countryId: null,
			currency: null,
			dailyPrice: null,
			defaultModuleIds: null,
			subscriptionModuleIds: null,
			tier: null,
			infoJson: null,
			packageIcon: null
		};
		if (!id) {
			return defaultValues;
		}
		try {
			const response = await axios.post(ADMIN.CONFIGURATION.GET_PACKAGE, { id });

			const { data, status } = response;

			if (status !== 200) {
				showError();
				handleCloseModal();
				return;
			}

			return {
				...defaultValues,
				...data,
				defaultModuleIds: data?.defaultModuleIds ? data?.defaultModuleIds?.split(',') : null,
				subscriptionModuleIds: data?.subscriptionModuleIds ? data?.subscriptionModuleIds?.split(',') : null
			};
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	}, [id]);

	// Form setup using react-hook-form
	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: fillForm
	});

	const {
		handleSubmit,
		setValue,
		watch,
		formState: { isDirty, isSubmitting, isSubmitSuccessful, isLoading: isFormLoading }
	} = formMethods;

	const loading = isFormLoading || isSubmitting;
	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful);

	const onSubmit = (data) => {
		const url = id ? ADMIN.CONFIGURATION.UPDATE_PACKAGE : ADMIN.CONFIGURATION.ADD_PACKAGE;
		axios
			.post(url, {
				id,
				...data,
				defaultModuleIds: data?.defaultModuleIds?.join(',') ?? null,
				subscriptionModuleIds: data?.subscriptionModuleIds?.join(',') ?? null
			})
			.then((response) => {
				const { status } = response;

				if (status === 200) {
					dispatch(updateDynamicListData(DYNAMICLISTS.PACKAGES));
					showSuccess();
					handleCloseModal();
				}
			})
			.catch((error) => {
				if (error.response?.data.code === 'error.OA001') {
					showError({
						message: 'error.thisCombinationAlreadyExists'
					});
				} else {
					AppLogger.error(error);
					showError();
				}
			});
	};

	const moduleAutocompleteColumns = useMemo(
		() => [
			{ accessor: 'ID', width: 100 },
			{ accessor: 'roleName', width: 1000 }
		],
		[]
	);
	const countryAutocompleteColumns = useMemo(
		() => [
			{ accessor: 'ID', width: 100 },
			{ accessor: 'nameEn', width: 1000 }
		],
		[]
	);

	return (
		<Modal fullwidth name={modalName} maxWidth="medium" className="modal-addPackage">
			<ModalTitle>
				<Typography variant="p1">addPackage</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem label="packageName" colSpan={defaultColSpan} isLoading={loading} required>
						<StringInput name="packageName" placeholder="enterPackageName" />
					</FormItem>
					<FormItem label="packageRoleID" colSpan={defaultColSpan} isLoading={loading} required>
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.SYSTEM_PACKAGES}
							value={watch('packageRoleID')}
							setValue={(value) => {
								setValue('packageRoleID', value?.ID ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							name="packageRoleID"
							columns={moduleAutocompleteColumns}
							selectionLabelAccessor={['roleName']}
						/>
					</FormItem>
					<FormItem label="monthlyDiscount" colSpan={defaultColSpan} isLoading={loading}>
						<IntegerInput
							name="monthlyDiscount"
							allowFloat
							decimalLimit={2}
							unit={watch(`currency`)}
							placeholder="enterMonthlyDiscount"
						/>
					</FormItem>
					<FormItem label="monthlyPrice" colSpan={defaultColSpan} isLoading={loading}>
						<IntegerInput
							name="monthlyPrice"
							allowFloat
							decimalLimit={2}
							unit={watch(`currency`)}
							placeholder="enterMonthlyPrice"
						/>
					</FormItem>
					<FormItem label="yearlyDiscount" colSpan={defaultColSpan} isLoading={loading}>
						<IntegerInput
							name="yearlyDiscount"
							allowFloat
							decimalLimit={2}
							unit={watch(`currency`)}
							placeholder="enterYearlyDiscount"
						/>
					</FormItem>
					<FormItem label="yearlyPrice" colSpan={defaultColSpan} isLoading={loading}>
						<IntegerInput
							name="yearlyPrice"
							allowFloat
							decimalLimit={2}
							unit={watch(`currency`)}
							placeholder="enterYearlyPrice"
						/>
					</FormItem>
					<FormItem label="country" colSpan={defaultColSpan} isLoading={loading} required>
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.COUNTRIES}
							columns={countryAutocompleteColumns}
							selectionLabelAccessor={['nameEn']}
							value={watch('countryId')}
							setValue={(value) => {
								setValue('countryId', value?.ID ?? null, { shouldDirty: true, shouldValidate: true });
							}}
							name="countryId"
						/>
					</FormItem>
					<FormItem label="currency" colSpan={defaultColSpan} isLoading={loading} required>
						<EnumSelect
							searchable={false}
							enumName={ENUMS.CURRENCY}
							name="currency"
							value={watch('currency')}
							setValue={(value) => {
								setValue(`currency`, value, { shouldValidate: true, shouldDirty: true });
							}}
							placeholder="selectCurrency"
						/>
					</FormItem>
					<FormItem label="dailyPrice" colSpan={defaultColSpan} isLoading={loading}>
						<IntegerInput
							name="dailyPrice"
							allowFloat
							decimalLimit={2}
							unit={watch(`currency`)}
							placeholder="enterDailyPrice"
						/>
					</FormItem>
					<FormItem label="tier" colSpan={defaultColSpan} isLoading={loading} required>
						<IntegerInput name="tier" placeholder="enterTier" />
					</FormItem>
					<FormItem label="defaultModuleIds" colSpan={defaultColSpan} isLoading={loading}>
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.SYSTEM_ROLES}
							value={watch('defaultModuleIds')}
							setValue={(value) => {
								setValue('defaultModuleIds', value?.map((item) => item.ID) ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							name="defaultModuleIds"
							columns={moduleAutocompleteColumns}
							selectionLabelAccessor={['roleName']}
							multipleSelection
						/>
					</FormItem>
					<FormItem label="subscriptionModuleIds" colSpan={defaultColSpan} isLoading={loading}>
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.SYSTEM_ROLES}
							value={watch('subscriptionModuleIds')}
							setValue={(value) => {
								setValue('subscriptionModuleIds', value?.map((item) => item.ID) ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							name="subscriptionModuleIds"
							columns={moduleAutocompleteColumns}
							selectionLabelAccessor={['roleName']}
							multipleSelection
						/>
					</FormItem>

					<FormItem colSpan={fullColSpan} label="icon" isLoading={loading}>
						<StringInput name="packageIcon" placeholder="icon" />
						<Image url={watch('packageIcon')} width="8rem" height="8rem" defaultUrl={defaultImage} />
					</FormItem>
					<FormItem label="infoJson" isLoading={loading}>
						<TextArea name="infoJson" rows="8" placeholder="enterInfoJson" maxlength={4096} />
					</FormItem>
				</Form>
			</ModalContent>

			<ModalActions>
				<Button label={'cancel'} variant="outline" onClick={handleCloseModal} />

				<Button
					loading={loading}
					label={id ? 'save' : 'button.add'}
					disabled={!isDirty}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
				/>
			</ModalActions>
		</Modal>
	);
});

export default AddPackageModal;
